import { create } from 'zustand';

/**
 * Admin UI state. Toasts, the sidebar, the command palette, and per-grid density
 * — nothing fetched from the server.
 */

let toastId = 0;

const readDensity = () => {
  try {
    return localStorage.getItem('ge:density') ?? 'comfortable';
  } catch {
    return 'comfortable';
  }
};

export const useUi = create((set, get) => ({
  sidebarOpen: false,
  paletteOpen: false,

  openSidebar: () => set({ sidebarOpen: true }),
  closeSidebar: () => set({ sidebarOpen: false }),
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  togglePalette: () => set((state) => ({ paletteOpen: !state.paletteOpen })),
  closePalette: () => set({ paletteOpen: false }),

  /** Density is a per-operator preference and survives a reload. */
  density: readDensity(),
  setDensity: (density) => {
    set({ density });
    try {
      localStorage.setItem('ge:density', density);
    } catch {
      // A lost preference is not worth an error.
    }
  },

  toasts: [],
  toast: ({ title, description, tone = 'default', duration = 4500, action }) => {
    const id = (toastId += 1);
    set((state) => ({ toasts: [...state.toasts, { id, title, description, tone, action }] }));
    if (duration > 0) setTimeout(() => get().dismissToast(id), duration);
    return id;
  },
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((entry) => entry.id !== id) })),

  /** A destructive action opens this rather than a window.confirm. */
  confirmation: null,
  confirm: (options) =>
    new Promise((resolve) => {
      set({
        confirmation: {
          ...options,
          resolve: (value) => {
            set({ confirmation: null });
            resolve(value);
          },
        },
      });
    }),
}));

export const toast = (options) => useUi.getState().toast(options);
export const confirm = (options) => useUi.getState().confirm(options);

export default useUi;
